import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { RunVersionPreview } from "@models/configVersion.model";

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  preview: RunVersionPreview | null;
}

export default function RunVersionPreviewDialog({ open, onClose, loading, preview }: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#232323" }}>Column interpretation</Typography>
          {preview && (
            <Typography sx={{ fontSize: 12, color: "#8c9097" }}>
              {preview.file_name ?? `Run ${preview.run_id}`} · pinned v{preview.pinned_version ?? "—"} vs current v
              {preview.current_version ?? "—"}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon sx={{ fontSize: 18, color: "#8c9097" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <Typography sx={{ fontSize: 13, color: "#8c9097" }}>Loading…</Typography>}
        {!loading && preview && (
          <>
            <Typography sx={{ fontSize: 13, color: "#536485", mb: 1.5 }}>
              {preview.changed_count === 0
                ? "This file is read identically under both versions."
                : `${preview.changed_count} column${preview.changed_count === 1 ? "" : "s"} read differently between the pinned and current config.`}
              {preview.columns_source === "config" && " (Source file unavailable — showing configured columns.)"}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f9fafb" }}>
                  {["Column", "Pinned", "Current"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.map((r) => (
                  <TableRow key={r.column} sx={{ bgcolor: r.changed ? "rgba(132,90,223,0.04)" : undefined }}>
                    <TableCell sx={{ fontSize: 13, fontWeight: 500, color: "#333335" }}>{r.column}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: r.changed ? "#845adf" : "#536485" }}>{r.pinned_role}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: r.changed ? "#845adf" : "#536485" }}>{r.current_role}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
